import * as Sentry from '@sentry/react';
import { APP_VERSION } from '@/lib/version';
import { Capacitor } from '@capacitor/core';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export function initSentry() {
  if (!SENTRY_DSN) {
    if (import.meta.env.DEV) console.warn('[Sentry] No DSN configured, skipping initialization');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: `mycue@${APP_VERSION}`,

    integrations: [
      Sentry.browserTracingIntegration(),
      // Only enable session replay on web — it can cause WebView issues on native
      ...(!Capacitor.isNativePlatform() ? [Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false })] : []),
    ],

    // Performance monitoring — sample 20% in prod
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,

    // Session replay — capture 5% normally, 100% on error
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,

    // Filter noisy errors
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection',
      'Load failed',
      'NetworkError',
      'ChunkLoadError',
      'Loading chunk',
      'Failed to fetch dynamically imported module',
    ],

    beforeSend(event) {
      // Tag platform info
      event.tags = {
        ...event.tags,
        platform: Capacitor.getPlatform(),
        native: String(Capacitor.isNativePlatform()),
      };
      // Add device context for native
      if (Capacitor.isNativePlatform()) {
        event.contexts = {
          ...event.contexts,
          device: {
            ...event.contexts?.device,
            screen_width: window.screen?.width,
            screen_height: window.screen?.height,
            pixel_ratio: window.devicePixelRatio,
          },
        };
      }
      return event;
    },
  });

  // Capture unhandled native WebView errors that might not bubble to React ErrorBoundary
  if (Capacitor.isNativePlatform()) {
    setupNativeCrashForwarding();
  }
}

/**
 * Forward native-layer unhandled errors/rejections to Sentry with extra context.
 * Covers crashes in Capacitor plugins, WebView errors, etc.
 */
function setupNativeCrashForwarding() {
  // Global error handler for native WebView
  window.addEventListener('error', (event) => {
    if (event.error) {
      Sentry.captureException(event.error, {
        tags: { source: 'native_global_error' },
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    }
  });

  // Unhandled promise rejections (Capacitor plugin failures, etc.)
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason ?? 'Unknown rejection'));
    Sentry.captureException(error, {
      tags: { source: 'native_unhandled_rejection' },
    });
  });

  // Listen for Capacitor App state changes to detect crash-recovery launches
  import('@capacitor/app').then(({ App }) => {
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        Sentry.addBreadcrumb({
          category: 'app.lifecycle',
          message: 'App resumed (foreground)',
          level: 'info',
        });
      } else {
        Sentry.addBreadcrumb({
          category: 'app.lifecycle',
          message: 'App backgrounded',
          level: 'info',
        });
      }
    });
  }).catch(() => {
    // @capacitor/app not available, skip
  });
}

/** Set the authenticated user context for Sentry */
export function setSentryUser(userId: string, email?: string) {
  Sentry.setUser({ id: userId, email });
}

/** Clear user context on logout */
export function clearSentryUser() {
  Sentry.setUser(null);
}
